import type { FiscalWorkstationStorage } from '@/entities/fiscalization';
import type { KkmExecutionResult, KkmServerAdapter } from '@/shared/api';

import { extractKkmFiscalDocument } from '@/entities/fiscalization';
import {
  type AnswerOrderFiscalReceiptPrintedInput,
  type CompleteOrderFiscalRefundInput,
  type FailOrderFiscalRefundInput,
  type OrderFieldsFragment,
  OrderFiscalFailureKind,
  OrderFiscalKkmCommandStatus,
  type OrderFiscalOperationIdentityInput,
  OrderFiscalOperationStatus,
  OrderFiscalRecoveryAction,
  type OrderFiscalRefundExecutionFieldsFragment,
  OrderFiscalRefundResultOutcome,
  type PrepareOrderFiscalRefundInput,
  type ResolveOrderFiscalReceiptInput,
  type RetryOrderFiscalReceiptInput,
} from '@shared/api/graphql/gql/graphql';

import type { OrderFiscalizationState } from './order-fiscalization-state';

import { executeKkmSafely, isProvenNotRun } from './execute-kkm-safely';
import { FiscalOperationController } from './fiscal-operation-controller';
import { orderFiscalEquipmentVerification } from './order-fiscal-equipment-verification';
import {
  mapFiscalExecutionReceipt,
  assertExecutionTarget,
  assertLiveKktReady,
  commandStatus,
  fiscalResultErrorFields,
  identity,
  requireReadyHandshake,
  resultOperatorMessage,
  ShiftReadinessError,
  toBankEvidenceInput,
} from './order-fiscal-result';
import { RESETTABLE_PHASES } from './order-fiscalization-state';

const REFUND_FALLBACK_MESSAGE = 'Не получилось вернуть деньги. Попробуйте ещё раз.';

export interface OrderFiscalRefundMutationResult {
  execution: OrderFiscalRefundExecutionFieldsFragment;
  order: OrderFieldsFragment;
}

/** Повтор, чтение ФН и ответ о чеке возвращают только шаг: исполнение перечитывается через `find`. */
export interface OrderFiscalRefundStep {
  next_action: OrderFiscalRecoveryAction;
  operation: { id: string; status: OrderFiscalOperationStatus };
}

export interface OrderFiscalRefundServices {
  answerReceiptPrinted: (input: AnswerOrderFiscalReceiptPrintedInput) => Promise<OrderFiscalRefundStep>;
  complete: (input: CompleteOrderFiscalRefundInput) => Promise<OrderFiscalRefundMutationResult>;
  fail: (input: FailOrderFiscalRefundInput) => Promise<OrderFiscalRefundMutationResult>;
  find: (orderId: string) => Promise<OrderFiscalRefundExecutionFieldsFragment | null>;
  prepare: (input: PrepareOrderFiscalRefundInput) => Promise<OrderFiscalRefundExecutionFieldsFragment>;
  resolveReceipt: (input: ResolveOrderFiscalReceiptInput) => Promise<OrderFiscalRefundStep>;
  retryReceipt: (input: RetryOrderFiscalReceiptInput) => Promise<OrderFiscalRefundStep>;
  start: (input: OrderFiscalOperationIdentityInput) => Promise<OrderFiscalRefundExecutionFieldsFragment>;
}

export interface OrderFiscalRefundControllerDependencies {
  adapter: Pick<KkmServerAdapter, 'closeShift' | 'execute' | 'getDataCheck' | 'getResult' | 'handshake' | 'openShift'>;
  /** Привязанная ККТ локации: без неё handshake не выберет кассу, когда их несколько. */
  timezone?: () => string | undefined;
  expectedKktDeviceId?: () => string | null | undefined;
  onState?: (state: Readonly<OrderFiscalizationState>) => void;
  services: OrderFiscalRefundServices;
  storage: FiscalWorkstationStorage;
}

export class OrderFiscalRefundController extends FiscalOperationController<
  OrderFiscalRefundExecutionFieldsFragment,
  CompleteOrderFiscalRefundInput
> {
  private readonly dependencies: OrderFiscalRefundControllerDependencies;

  public constructor(dependencies: OrderFiscalRefundControllerDependencies) {
    super(
      dependencies.adapter,
      (state) => dependencies.onState?.(state),
      {
        captured: 'Деньги возвращены, чек возврата не подтверждён. Нажмите «Напечатать чек»: Mors проверит кассу.',
        unknown: 'Непонятно, вышел ли чек возврата. Нажмите «Напечатать чек»: Mors проверит кассу.',
        question: 'Касса не дала прочитать документы. Посмотрите на кассу: вышел ли чек возврата?',
        fallback: REFUND_FALLBACK_MESSAGE,
        finalizing: 'Чек возврата напечатан. Сохраняем возврат.',
        pendingSync: 'Чек возврата напечатан, но возврат не записался. Нажмите «Сохранить возврат».',
        invalidResult: 'Касса ответила непонятно. Откройте заказ заново.',
        retry: 'Деньги клиенту возвращены, чек возврата не вышел. Проверьте бумагу и нажмите «Повторить печать».',
        failedPrevious: 'Предыдущая попытка закрыта. Можно вернуться к возврату.',
      },
      dependencies,
    );
    this.dependencies = dependencies;
  }

  public async refund(
    input: Omit<PrepareOrderFiscalRefundInput, 'browserWorkstationId' | 'kktDeviceId' | 'kkt' | 'terminal'>,
  ): Promise<OrderFieldsFragment | null> {
    if (this.busy) return null;
    this.busy = true;

    try {
      this.transition('checking_readiness');
      const handshake = await this.handshakeBoundKkt(this.dependencies.expectedKktDeviceId?.());
      if (handshake.kind !== 'ready') throw new Error(handshake.instruction);
      assertLiveKktReady(handshake.kkt, this.dependencies.timezone?.());
      this.liveKkt.capture(handshake);

      this.transition('preparing');
      const prepared = await this.dependencies.services.prepare({
        ...input,
        kktDeviceId: handshake.kktDevice.idDevice,
        ...orderFiscalEquipmentVerification(handshake),
      });
      this.dependencies.storage.addPendingOperation(prepared.operation.id);
      if (prepared.execution_target.kkt_device_id !== handshake.kktDevice.idDevice) {
        throw new Error('К этому компьютеру подключена другая касса. Возврат сделайте на кассе исходной локации.');
      }
      return await this.runPrepared(prepared);
    } catch (error) {
      if (this.stateValue.phase === 'checking_readiness' || this.stateValue.phase === 'preparing') {
        this.transition(
          'failed_before_print',
          this.errorMessage(error),
          undefined,
          error instanceof ShiftReadinessError ? error.action : null,
        );
      } else {
        this.transition('print_result_unknown', this.errorMessage(error));
      }
      return null;
    } finally {
      this.busy = false;
    }
  }

  /** `startPayment: false` — цикл смены: возврат не начинается, чек проверяется по ФН без вопроса. */
  protected override async recoverOrder(
    execution: OrderFiscalRefundExecutionFieldsFragment | null,
    _orderId: string,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null> {
    if (execution === null) return null;
    this.dependencies.storage.addPendingOperation(execution.operation.id);

    return await this.continueRecovery(execution, cycle);
  }

  protected override async answerReceiptFor(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    printed: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const step = await this.dependencies.services.answerReceiptPrinted({
      ...identity(execution, execution.execution_target.kkt_device_id),
      printed,
    });
    return await this.continueAfterReceiptDecision(execution, step);
  }

  private async continueRecovery(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    cycle: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const receiptStep = this.recoverReceiptStep(execution, cycle);
    if (receiptStep !== undefined) return receiptStep;

    await this.captureLiveIdentity(execution);

    if (execution.next_action === OrderFiscalRecoveryAction.StartPayment) {
      if (cycle) {
        await this.failKnownPrePrint(execution, 'shift_closing', 'Возврат не начинался: смена закрывается.');
        this.transition('failed_before_print', 'Возврат не начинался. Деньги не возвращены.', execution);
        return null;
      }
      return this.runPrepared(execution);
    }
    if (execution.next_action === OrderFiscalRecoveryAction.PrintReceipt) return this.print(execution);
    if (execution.next_action === OrderFiscalRecoveryAction.PollResult) {
      const polled = await this.pollPrepared(execution);
      if (polled === null) return null;
      const { hasCard, result } = polled;
      if (hasCard) return this.finishBank(execution, result);
      return this.finishPrint(execution, result);
    }

    return null;
  }

  private async runPrepared(execution: OrderFiscalRefundExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    // Как у оплаты: повтор prepare отдаёт уже начатый возврат, второй раз команда терминалу не уходит.
    if (execution.operation.status !== OrderFiscalOperationStatus.Prepared)
      return this.continueRecovery(execution, false);

    const mapped = await this.mapPrepared(execution);

    if (mapped === null) return null;
    const started = await this.dependencies.services.start(this.liveKkt.identityFor(execution));
    if (mapped.acquiringCommand !== null && mapped.acquiringDeviceKey !== null) {
      this.transition('waiting_payment', 'Верните деньги клиенту на терминале.', started);
      const bankResult = await executeKkmSafely(() =>
        this.dependencies.adapter.execute(mapped.acquiringCommand as NonNullable<typeof mapped.acquiringCommand>, {
          deviceKey: mapped.acquiringDeviceKey as string,
          timeoutClass: 'acquiring',
        }),
      );
      return this.finishBank(started, bankResult);
    }
    return this.print(started);
  }

  private recordDefinitiveFailure(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    result: KkmExecutionResult,
    failureKind: OrderFiscalFailureKind,
  ): Promise<OrderFiscalRefundMutationResult> {
    return this.dependencies.services.fail({
      ...this.liveKkt.identityFor(execution),
      commandStatus: commandStatus(result),
      ...fiscalResultErrorFields(result, execution.operation.id),
      failureKind,
    });
  }

  private recordAmbiguousResult(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    result: KkmExecutionResult,
  ): Promise<OrderFiscalRefundMutationResult> {
    return this.dependencies.services.complete({
      ...this.liveKkt.identityFor(execution),
      result: {
        commandStatus: commandStatus(result),
        ...fiscalResultErrorFields(result, execution.operation.id),
        outcome: OrderFiscalRefundResultOutcome.Ambiguous,
      },
    });
  }

  private async finishBank(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    result: KkmExecutionResult,
  ): Promise<OrderFieldsFragment | null> {
    if (result.kind !== 'ok' || result.acquiring === null) {
      if (isProvenNotRun(result) || (result.kind === 'error' && result.error.outcome === 'user_cancelled')) {
        const cancelled = result.kind === 'error' && result.error.kind === 'user_cancelled';
        console.warn('[OrderFiscalRefundController.definitive-not-run]', {
          operationId: execution.operation.id,
          branch: 'refund_card',
          resultKind: result.kind,
        });
        await this.recordDefinitiveFailure(
          execution,
          result,
          cancelled ? OrderFiscalFailureKind.PaymentCancelled : OrderFiscalFailureKind.KnownPrePrint,
        );
        this.dependencies.storage.clearPendingOperation(execution.operation.id);
        if (cancelled) {
          // Сырой текст терминала кассиру не нужен: он в подробностях и журнале.
          this.transition(
            'cancelled_on_terminal',
            'Деньги не возвращены.',
            execution,
            null,
            result.error.vendorMessage,
          );
          return null;
        }
        this.transition(
          'failed_before_print',
          resultOperatorMessage(
            result,
            result.kind === 'error'
              ? result.error.message
              : 'Терминал не выполнил возврат. Деньги не возвращены — попробуйте ещё раз.',
          ),
          execution,
        );
        return null;
      }
      // Неясный возврат по карте считается выполненным: двойной возврат никто не заметит,
      // а недошедший клиент заметит и обратится. Дальше печатается чек возврата.
      const unverified = await this.recordAmbiguousResult(execution, result);
      if (unverified.execution.operation.status === OrderFiscalOperationStatus.PaymentCaptured) {
        console.warn('[OrderFiscalRefundController.refund-unverified]', { operationId: execution.operation.id });
        return this.print(unverified.execution);
      }
      this.showResolveReceipt(unverified.execution);
      return null;
    }

    const returned = await this.dependencies.services.complete({
      ...this.liveKkt.identityFor(execution),
      result: {
        bank: toBankEvidenceInput(result.acquiring),
        commandStatus: OrderFiscalKkmCommandStatus.Ok,
        outcome: OrderFiscalRefundResultOutcome.RefundCompleted,
      },
    });
    return this.print(returned.execution);
  }

  private async print(execution: OrderFiscalRefundExecutionFieldsFragment): Promise<OrderFieldsFragment | null> {
    const result = await this.executeReceipt(execution, 'Касса печатает чек возврата.');
    return this.finishPrint(execution, result);
  }

  private async finishPrint(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    result: KkmExecutionResult,
  ): Promise<OrderFieldsFragment | null> {
    const { kktShiftClosedExternally, unreceipted } = await this.completePrintFailure(
      execution,
      result,
      OrderFiscalRefundResultOutcome.RefundCompletedUnfiscalized,
      (input) => this.dependencies.services.complete(input),
    );

    if (result.kind !== 'ok') {
      if (unreceipted !== null) {
        if (this.showReceiptRetry(unreceipted)) return null;
        this.resolvableExecution = unreceipted;
        this.transition(
          'payment_without_receipt',
          resultOperatorMessage(
            result,
            kktShiftClosedExternally
              ? 'Деньги возвращены, чек не вышел, а смена на кассе была закрыта извне. Откройте смену и нажмите «Напечатать чек».'
              : 'Деньги клиенту возвращены, чек возврата не подтверждён. Нажмите «Напечатать чек».',
          ),
          unreceipted,
          kktShiftClosedExternally ? 'reopen' : null,
        );
        return null;
      }

      if (isProvenNotRun(result)) {
        console.warn('[OrderFiscalRefundController.definitive-not-run]', {
          operationId: execution.operation.id,
          branch: 'refund_kkt',
          resultKind: result.kind,
        });
        await this.recordDefinitiveFailure(execution, result, OrderFiscalFailureKind.KnownPrePrint);
        this.dependencies.storage.clearPendingOperation(execution.operation.id);
        this.transition(
          'failed_before_print',
          resultOperatorMessage(
            result,
            kktShiftClosedExternally
              ? 'Смена на кассе была закрыта извне. Смена в Морсе продолжается.'
              : result.kind === 'error'
                ? result.error.message
                : 'Касса не выполнила команду. Чек возврата не напечатан — попробуйте ещё раз.',
          ),
          execution,
          kktShiftClosedExternally ? 'reopen' : null,
        );
        return null;
      }

      const unresolved = await this.recordAmbiguousResult(execution, result);
      this.resolvableExecution = unresolved.execution;
      this.transition(
        'print_result_unknown',
        resultOperatorMessage(
          result,
          kktShiftClosedExternally
            ? 'Смена на кассе была закрыта извне, а результат чека возврата неясен. Откройте смену и нажмите «Напечатать чек».'
            : 'Непонятно, вышел ли чек возврата. Нажмите «Напечатать чек»: Mors проверит кассу.',
        ),
        unresolved.execution,
        kktShiftClosedExternally ? 'reopen' : null,
      );
      return null;
    }

    const fiscal = extractKkmFiscalDocument(result.response, execution.execution_target.timezone);
    const input: CompleteOrderFiscalRefundInput = {
      ...this.liveKkt.identityFor(execution),
      result:
        fiscal === null
          ? {
              commandStatus: OrderFiscalKkmCommandStatus.Ok,
              errorCode: 'malformed_fiscal_result',
              outcome: OrderFiscalRefundResultOutcome.Ambiguous,
            }
          : {
              commandStatus: OrderFiscalKkmCommandStatus.Ok,
              fiscal,
              outcome: OrderFiscalRefundResultOutcome.Fiscalized,
            },
    };
    return this.savePrintedReceipt(execution, input);
  }

  /** Чек возврата ищется в документах ФН; логика та же, что у оплаты. */
  protected override async resolveReceiptFor(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    ask: boolean,
  ): Promise<OrderFieldsFragment | null> {
    const read = await this.readReceiptDocuments(execution);
    if (read.kind === 'unavailable') {
      this.transition('kkt_unavailable', 'Деньги возвращены. Чек напечатаем, когда касса ответит.', execution);
      return null;
    }
    if (read.kind === 'unreadable') {
      console.warn('[OrderFiscalRefundController.receipt-unresolved]', { operationId: execution.operation.id });
      if (ask) this.showReceiptQuestion(execution);
      else this.showResolveReceipt(execution);
      return null;
    }

    const step = await this.dependencies.services.resolveReceipt({
      ...identity(execution, execution.execution_target.kkt_device_id),
      ...read.input,
    });

    return this.continueAfterReceiptDecision(execution, step);
  }

  /** Шаг решения по чеку: исход берётся из мутации, план повтора — из `find`. */
  private async continueAfterReceiptDecision(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    step: OrderFiscalRefundStep,
  ): Promise<OrderFieldsFragment | null> {
    this.resolvableExecution = null;
    this.receiptQuestionOpen = false;

    if (step.operation.status === OrderFiscalOperationStatus.Completed) {
      this.dependencies.storage.clearPendingOperation(execution.operation.id);
      this.transition('completed', null, execution);
      return null;
    }

    if (step.operation.status === OrderFiscalOperationStatus.Failed) {
      this.dependencies.storage.clearPendingOperation(execution.operation.id);
      this.transition(
        'failed_before_print',
        'Чека возврата нет, возврат не записан. Проведите его ещё раз.',
        execution,
      );
      return null;
    }

    const current = await this.dependencies.services.find(execution.operation.order_id);

    if (current === null) throw new Error('Mors не вернул операцию возврата.');
    if (step.next_action === OrderFiscalRecoveryAction.PrintReceipt) return this.retryAndPrint(current);

    this.showResolveReceipt(current);
    return null;
  }

  protected override async retryAndPrint(
    execution: OrderFiscalRefundExecutionFieldsFragment,
  ): Promise<OrderFieldsFragment | null> {
    this.transition('checking_readiness', 'Проверяем кассу.', execution);
    const ready = requireReadyHandshake(await this.handshakeBoundKkt(execution.execution_target.kkt_device_id));
    assertExecutionTarget(execution, ready, 'Подключите кассу операции.');
    assertLiveKktReady(ready.kkt, execution.execution_target.timezone);
    this.liveKkt.capture(ready);
    await this.dependencies.services.retryReceipt({
      ...this.liveKkt.identityFor(execution),
      kkt: orderFiscalEquipmentVerification(ready).kkt,
    });
    const retried = await this.dependencies.services.find(execution.operation.order_id);

    if (retried === null) throw new Error('Mors не вернул операцию возврата после повтора.');

    return this.print(retried);
  }

  private async failKnownPrePrint(
    execution: OrderFiscalRefundExecutionFieldsFragment,
    errorCode: string,
    errorMessage: string,
  ): Promise<void> {
    await this.dependencies.services.fail({
      ...this.liveKkt.identityFor(execution),
      commandStatus: OrderFiscalKkmCommandStatus.NotRun,
      errorCode,
      errorMessage,
      failureKind: OrderFiscalFailureKind.KnownPrePrint,
    });
    this.dependencies.storage.clearPendingOperation(execution.operation.id);
  }

  /**
   * Mapping отклоняет неподдержанный план уже после успешного prepare: закрываем
   * операцию fail-мутацией сразу, иначе она держит возврат до истечения TTL.
   */
  protected override async mapPrepared(
    execution: OrderFiscalRefundExecutionFieldsFragment,
  ): Promise<ReturnType<typeof mapFiscalExecutionReceipt> | null> {
    try {
      return mapFiscalExecutionReceipt(execution);
    } catch (error) {
      console.error('[OrderFiscalRefundController.mapping-unsupported]', {
        operationId: execution.operation.id,
        reason: this.errorMessage(error),
      });

      try {
        await this.failKnownPrePrint(execution, 'mapping_unsupported', this.errorMessage(error));
      } catch (failError) {
        console.error('[OrderFiscalRefundController.mapping-fail-mutation-failed]', {
          operationId: execution.operation.id,
          reason: this.errorMessage(failError),
        });
      }

      this.transition('failed_before_print', this.errorMessage(error), execution);

      return null;
    }
  }

  /**
   * `selectKktDevice` требует ровно одного кандидата, поэтому без явного
   * `kktDeviceId` локация с несколькими ККТ не доходит до prepare.
   */
  protected override async handshakeBoundKkt(kktDeviceId: string | null | undefined) {
    console.warn('[OrderFiscalRefundController.handshake]', {
      hasBoundKkt: Boolean(kktDeviceId),
    });

    return this.dependencies.adapter.handshake(kktDeviceId ? { kktDeviceId } : {});
  }

  private async captureLiveIdentity(execution: OrderFiscalRefundExecutionFieldsFragment): Promise<void> {
    const ready = requireReadyHandshake(await this.handshakeBoundKkt(execution.execution_target.kkt_device_id));
    assertExecutionTarget(execution, ready, 'Возврат сделайте на кассе исходной локации.');
    this.liveKkt.capture(ready);
  }

  protected override transition(
    phase: OrderFiscalizationState['phase'],
    message: string | null = null,
    execution?: OrderFiscalRefundExecutionFieldsFragment,
    shiftAction: OrderFiscalizationState['shiftAction'] = null,
    technicalDetails: string | null = null,
  ): void {
    this.stateValue = {
      amount: execution?.receipt_plan.total_amount ?? this.stateValue.amount,
      canRetryReceipt: this.retryableExecution !== null,
      canResolveReceipt:
        this.resolvableExecution !== null &&
        !this.receiptQuestionOpen &&
        (phase === 'payment_without_receipt' || phase === 'print_result_unknown' || phase === 'kkt_unavailable'),
      receiptQuestion: this.receiptQuestionOpen && phase === 'print_result_unknown',
      cashToReturn: 0,
      canReset: RESETTABLE_PHASES.has(phase),
      cancellable: false,
      message,
      canCancelPayment: false,
      phase,
      shiftAction,
      terminalName: execution?.operation.workstation.name ?? this.stateValue.terminalName,
      technicalDetails,
      // Признак владения читается только при оплате: возврат его не меняет.
      unownedMarkingCodes: [],
    };
    this.emitState();
  }
}
